import axios from 'axios';
import { Contact, CreateContactDto, UpdateContactDto, ContactStats, ContactStatus } from '../types/contact';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface PaginatedResponse<T> {
  contacts: T[];
  total: number;
  page: number;
  totalPages: number;
}

export const contactService = {
  async getAllContacts(params?: { 
    status?: ContactStatus; 
    isRead?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Contact>> {
    // Only include parameters that are defined
    const queryParams: any = {};
    if (params?.status) {
      queryParams.status = params.status;
    }
    if (params?.isRead !== undefined) {
      queryParams.isRead = params.isRead;
    }
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.limit) {
      queryParams.limit = params.limit;
    }

    const response = await api.get('/contacts', { 
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined 
    });
    return response.data;
  },

  async getContactById(id: string): Promise<Contact> {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  },

  async createContact(contact: CreateContactDto): Promise<Contact> {
    const response = await api.post('/contacts', contact);
    return response.data;
  },

  async updateContact(id: string, contact: UpdateContactDto): Promise<Contact> {
    const response = await api.patch(`/contacts/${id}`, contact);
    return response.data;
  },

  async deleteContact(id: string): Promise<void> {
    await api.delete(`/contacts/${id}`);
  },

  async getUnreadCount(): Promise<number> {
    const response = await api.get('/contacts/unread/count');
    return response.data;
  },

  async markAsRead(id: string): Promise<Contact> {
    const response = await api.patch(`/contacts/${id}/read`);
    return response.data;
  },

  async assignContact(id: string, userId: string): Promise<Contact> {
    const response = await api.patch(`/contacts/${id}/assign/${userId}`);
    return response.data;
  },

  async updateStatus(id: string, status: ContactStatus): Promise<Contact> {
    const response = await api.patch(`/contacts/${id}/status/${status}`);
    return response.data;
  },

  async getAssignedContacts(userId: string): Promise<Contact[]> {
    const response = await api.get(`/contacts/assigned/${userId}`);
    return response.data;
  },

  async getContactStats(): Promise<ContactStats> {
    const response = await api.get('/contacts/stats');
    return response.data;
  }
}; 